'use client';

import { useEffect, useRef, useState } from 'react';

interface TurnstileCaptchaProps {
  sitekey: string;
  onVerify: (token: string) => Promise<boolean>;
  onError?: () => void;
}

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: {
        sitekey: string;
        callback?: (token: string) => void;
        'error-callback'?: () => void;
        'expired-callback'?: () => void;
        theme?: 'light' | 'dark' | 'auto';
      }) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

export function TurnstileCaptcha({ sitekey, onVerify, onError }: TurnstileCaptchaProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isVerified, setIsVerified] = useState(false);

  useEffect(() => {
    // 检查浏览器环境（增强检测）
    if (typeof window === 'undefined' || 
        typeof navigator === 'undefined' || 
        typeof document === 'undefined' ||
        typeof crypto === 'undefined' ||
        typeof crypto.subtle === 'undefined') {
      if (onError) onError();
      return;
    }

    // 加载 Turnstile 脚本
    const loadTurnstile = () => {
      if (window.turnstile) {
        initTurnstile();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        if (window.turnstile) {
          setIsLoading(false);
          initTurnstile();
        } else {
          setIsLoading(false);
          if (onError) onError();
        }
      };

      script.onerror = () => {
        setIsLoading(false);
        if (onError) onError();
      };

      document.head.appendChild(script);
    };

    const initTurnstile = () => {
      if (!containerRef.current || !window.turnstile || !sitekey) {
        return;
      }

      try {
        const widgetId = window.turnstile.render(containerRef.current, {
          sitekey,
          callback: async (token: string) => {
            setIsVerified(true);
            const success = await onVerify(token);
            if (!success) {
              setIsVerified(false);
              if (widgetIdRef.current && window.turnstile) {
                window.turnstile.reset(widgetIdRef.current);
              }
            }
          },
          'error-callback': () => {
            setIsVerified(false);
            if (onError) onError();
          },
          'expired-callback': () => {
            setIsVerified(false);
            if (widgetIdRef.current && window.turnstile) {
              window.turnstile.reset(widgetIdRef.current);
            }
          },
          theme: 'auto',
        });

        widgetIdRef.current = widgetId;
      } catch (error) {
        console.error('Turnstile initialization error:', error);
        if (onError) onError();
      }
    };

    loadTurnstile();

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (error) {
          console.error('Turnstile cleanup error:', error);
        }
      }
    };
  }, [sitekey, onVerify, onError]);

  if (typeof window === 'undefined') {
    return (
      <div className="p-4 text-center text-sm text-gray-500">
        请在浏览器环境中使用此验证码
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-4 text-center text-sm text-gray-500">
        加载安全验证中...
      </div>
    );
  }

  return (
    <div className="flex justify-center">
      <div ref={containerRef} className="turnstile-container" />
      {isVerified && (
        <div className="ml-2 flex items-center text-green-600 dark:text-green-400">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        </div>
      )}
    </div>
  );
}

